import { Observable } from 'rxjs';

import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { ApiUrlInjectionToken } from '@mitsubishi/shared/data-access';

import {
  AddLooseAccessoryRequestDto,
  CompatibleAccessoriesQueryRequestDto,
  CompatibleAccessoriesQueryResponseDto,
  CompatibleAccessoryCountsResponseDto,
  LooseAccessoryRowDto,
  ToggleAlternativeLinkRequestDto,
  UpdateLooseAccessoryQuantityRequestDto,
} from '../models/cross-selling.model';

/**
 * Thin client for the cross-selling endpoints, one method per operation. The mutations answer the caller only: every
 * connected user, the caller included, gets the change through the selection JSON diff.
 */
@Injectable({
  providedIn: 'root',
})
export class CrossSellingService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${inject(ApiUrlInjectionToken)}/project`;

  getCompatibleAccessoryCounts(
    projectId: string,
    selectionId: string
  ): Observable<CompatibleAccessoryCountsResponseDto> {
    return this.http.get<CompatibleAccessoryCountsResponseDto>(
      `${this.selectionUrl(projectId, selectionId)}/cross-selling/compatible-accessories/counts`
    );
  }

  queryCompatibleAccessories(
    projectId: string,
    selectionId: string,
    request: CompatibleAccessoriesQueryRequestDto
  ): Observable<CompatibleAccessoriesQueryResponseDto> {
    return this.http.post<CompatibleAccessoriesQueryResponseDto>(
      `${this.selectionUrl(projectId, selectionId)}/cross-selling/compatible-accessories/query`,
      request
    );
  }

  addLooseAccessory(
    projectId: string,
    selectionId: string,
    request: AddLooseAccessoryRequestDto
  ): Observable<LooseAccessoryRowDto> {
    return this.http.post<LooseAccessoryRowDto>(
      `${this.selectionUrl(projectId, selectionId)}/loose-accessory`,
      request
    );
  }

  deleteLooseAccessory(projectId: string, selectionId: string, looseAccessoryId: string): Observable<void> {
    return this.http.delete<void>(this.looseAccessoryUrl(projectId, selectionId, looseAccessoryId));
  }

  updateLooseAccessoryQuantity(
    projectId: string,
    selectionId: string,
    looseAccessoryId: string,
    request: UpdateLooseAccessoryQuantityRequestDto
  ): Observable<void> {
    return this.http.put<void>(`${this.looseAccessoryUrl(projectId, selectionId, looseAccessoryId)}/quantity`, request);
  }

  linkLooseAccessoryAlternative(
    projectId: string,
    selectionId: string,
    looseAccessoryId: string,
    request: ToggleAlternativeLinkRequestDto
  ): Observable<void> {
    return this.http.post<void>(
      `${this.looseAccessoryUrl(projectId, selectionId, looseAccessoryId)}/linkAlternative`,
      request
    );
  }

  unlinkLooseAccessoryAlternative(
    projectId: string,
    selectionId: string,
    looseAccessoryId: string,
    request: ToggleAlternativeLinkRequestDto
  ): Observable<void> {
    return this.http.post<void>(
      `${this.looseAccessoryUrl(projectId, selectionId, looseAccessoryId)}/unlinkAlternative`,
      request
    );
  }

  private selectionUrl(projectId: string, selectionId: string) {
    return `${this.baseUrl}/${projectId}/selection/${selectionId}`;
  }

  private looseAccessoryUrl(projectId: string, selectionId: string, looseAccessoryId: string) {
    return `${this.selectionUrl(projectId, selectionId)}/loose-accessory/${looseAccessoryId}`;
  }
}
